import { useState } from "react";
import { CalendarClock, Play, Power, Trash2 } from "lucide-react";
import { MmcFrame } from "../../admin-framework/MmcFrame.js";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

const scheduledRows = [
  { name: "Daily Sync", status: "Ready", trigger: "Daily 08:00", nextRun: "Tomorrow 08:00", lastRun: "Today 08:00" },
  { name: "CleanupCache", status: "Disabled", trigger: "At logon", nextRun: "-", lastRun: "Yesterday 09:11" },
  { name: "FD-Scheduled-Task", status: "Running", trigger: "One time", nextRun: "Today 17:00", lastRun: "Never" }
];

export function TaskSchedulerApp({ activeMachine, onRunCommand }: SimulatorAppSurfaceProps) {
  const [taskName, setTaskName] = useState("Daily Sync");
  const [trigger, setTrigger] = useState("Daily");
  const [action, setAction] = useState("sync");
  const [enabled, setEnabled] = useState(true);

  async function createTask(nextTaskName = taskName) {
    await onRunCommand({
      appId: "taskschd",
      commandType: "TASK_CREATE",
      capability: "local.scheduledTask.write",
      label: nextTaskName,
      payload: { action, machineId: activeMachine, taskName: nextTaskName, trigger }
    });
  }

  async function runTask(nextTaskName = taskName) {
    await onRunCommand({
      appId: "taskschd",
      commandType: "TASK_RUN_NOW",
      capability: "local.scheduledTask.execute",
      label: `Run ${nextTaskName}`,
      payload: { machineId: activeMachine, taskName: nextTaskName }
    });
  }

  async function toggleTask(nextEnabled = enabled, nextTaskName = taskName) {
    await onRunCommand({
      appId: "taskschd",
      commandType: "TASK_TOGGLE",
      capability: "local.scheduledTask.write",
      label: `${nextEnabled ? "Enable" : "Disable"} ${nextTaskName}`,
      payload: { enabled: nextEnabled, machineId: activeMachine, taskName: nextTaskName }
    });
  }

  async function deleteTask(nextTaskName = taskName) {
    await onRunCommand({
      appId: "taskschd",
      commandType: "TASK_DELETE",
      capability: "local.scheduledTask.write",
      label: `Delete ${nextTaskName}`,
      payload: { machineId: activeMachine, taskName: nextTaskName }
    });
  }

  return (
    <div className="fd-sim-app fd-sim-mmc">
      <MmcFrame
        actions={[
          { label: "Create Task", onClick: () => void createTask() },
          { label: "Run", onClick: () => void runTask() },
          { label: "Enable/Disable", onClick: () => void toggleTask(!enabled) },
          { label: "Delete", onClick: () => void deleteTask() }
        ]}
        columns={[
          { key: "name", label: "Name" },
          { key: "status", label: "Status" },
          { key: "trigger", label: "Triggers" },
          { key: "nextRun", label: "Next Run Time" },
          { key: "lastRun", label: "Last Run Time" }
        ]}
        rows={scheduledRows.map((task) => ({
          id: task.name,
          onDoubleClick: () => {
            setTaskName(task.name);
            void runTask(task.name);
          },
          values: {
            lastRun: task.lastRun,
            name: <button className="fd-mmc-link" type="button" onClick={() => setTaskName(task.name)}>{task.name}</button>,
            nextRun: task.nextRun,
            status: task.status,
            trigger: task.trigger
          }
        }))}
        title="Task Scheduler"
        tree={[
          { active: true, icon: <CalendarClock size={15} />, id: "library", label: "Task Scheduler Library" },
          { id: "system", label: "System Tasks" },
          { id: "custom", label: "Custom Tasks" }
        ]}
      >
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Selected task</p>
              <h3>{taskName}</h3>
            </div>
            <div className="fd-mmc-inline-actions">
              <button type="button" onClick={() => void runTask()}><Play size={14} /> Run</button>
              <button type="button" onClick={() => void toggleTask(!enabled)}><Power size={14} /> Toggle</button>
              <button type="button" onClick={() => void deleteTask()}><Trash2 size={14} /> Delete</button>
            </div>
          </header>
          <div className="fd-command-form">
            <label>
              Task name
              <input value={taskName} onChange={(event) => setTaskName(event.target.value)} />
            </label>
            <label>
              Trigger
              <select value={trigger} onChange={(event) => setTrigger(event.target.value)}>
                <option>Daily</option>
                <option>At logon</option>
                <option>One time</option>
              </select>
            </label>
            <label>
              Action
              <input value={action} onChange={(event) => setAction(event.target.value)} />
            </label>
            <label>
              Enabled
              <select value={enabled ? "enabled" : "disabled"} onChange={(event) => setEnabled(event.target.value === "enabled")}>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <button type="button" onClick={() => void createTask()}>Create task</button>
          </div>
        </section>
      </MmcFrame>
    </div>
  );
}
